import classNames from 'classnames';

import Icon from '@components/atoms/icon';
import {
  type AvailabilitySeverity,
  type PlayerStatus,
  getAvailabilityLabel,
  getAvailabilitySeverity,
  getChanceOfPlayingLabel
} from '@lib/my-team/player-availability';

// Amber for a mild doubt, red for anything worse. Mirrors the pitch tile name
// row.
const SEVERITY_STYLES: Record<Exclude<AvailabilitySeverity, 'none'>, string> = {
  caution: 'bg-warning-400 text-neutral-900',
  danger: 'bg-danger-600 text-white'
};

type BadgePlacement = 'image' | 'inline';

const PLACEMENT_STYLES: Record<BadgePlacement, string> = {
  image: 'absolute bottom-0 left-1/2 -translate-x-1/2 flex',
  inline: 'inline-flex'
};

type PlayerAvailabilityBadgeProps = {
  status: PlayerStatus | undefined;
  chanceOfPlaying: number | null | undefined;
  placement?: BadgePlacement;
  className?: string;
};

/**
 * Flags a doubtful or unavailable player with their chance of playing
 * ("75%"), or a warning icon when FPL gives no percentage. Renders nothing for
 * a fit player.
 *
 * With the default `image` placement it sits on the bottom edge of the
 * player's kit or face, so the image's container must be positioned
 * (`relative`). Use `inline` where the image is too small to carry it.
 */
const PlayerAvailabilityBadge = ({
  status,
  chanceOfPlaying,
  placement = 'image',
  className
}: PlayerAvailabilityBadgeProps) => {
  const severity = getAvailabilitySeverity(status, chanceOfPlaying);
  if (severity === 'none') return null;

  const label = getAvailabilityLabel(status, chanceOfPlaying);
  const chanceOfPlayingLabel = getChanceOfPlayingLabel(chanceOfPlaying);

  // 11px leading makes the chip 13px tall, an odd height like the 15px caption
  // line it sits in inline, so centring it lands on whole pixels and its text
  // shares the caption's baseline. The weight is repeated on the inner span
  // because `.content-body span` resets spans on article-style pages to 400.
  return (
    <span
      className={classNames(
        'items-center rounded px-1 py-px text-[10px] font-bold leading-[11px] whitespace-nowrap',
        PLACEMENT_STYLES[placement],
        SEVERITY_STYLES[severity],
        className
      )}
      title={label ?? undefined}
      data-cy="player-availability-badge"
      data-cy-availability={severity}
    >
      <span aria-hidden className="font-bold">
        {chanceOfPlayingLabel ?? (
          <Icon name="Warning" className="w-2.5 h-2.5" />
        )}
      </span>
      <span className="sr-only">{label}</span>
    </span>
  );
};

export default PlayerAvailabilityBadge;
